import { useEffect, useMemo, useRef, useState } from 'react'
import { useProducts } from '../../hooks/useFirestore'
import { usePermissions } from '../../hooks/usePermissions'
import { ACTIONS, MODULES } from '../../constants/permissions'
import { createDocument, deleteDocument, getCollection, updateDocument } from '../../services/firestore.service'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import { formatCurrency } from '../../utils/helpers'
import { generateLabelsPDF } from '../../utils/pdfGenerator'

const PAGE_SIZES = {
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
  Carta: { width: 216, height: 279 }
}

const FIELD_LABELS = {
  logo: 'Logo',
  name: 'Nombre',
  variant: 'Variante',
  price: 'Precio',
  sku: 'SKU',
  serial: 'Serie',
  barcode: 'Código de barras',
  qr: 'QR'
}

const FIELD_ORDER = ['logo', 'name', 'variant', 'price', 'sku', 'serial', 'barcode', 'qr']

const buildVariantLabel = (variant) => {
  if (!variant?.selection) return 'Sin variantes'
  const color = variant.selection.colorLabel || variant.selection.color
  const size = variant.selection.size
  return [color, size].filter(Boolean).join(' | ') || 'Sin variantes'
}

function AdminLabels() {
  const { can } = usePermissions()
  const { products, loading, error } = useProducts({ orderByField: 'createdAt', orderDirection: 'desc' }, [])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProductId, setSelectedProductId] = useState('')
  const [selectedVariantKeys, setSelectedVariantKeys] = useState([])
  const [variantQuantities, setVariantQuantities] = useState({})
  const [designName, setDesignName] = useState('')
  const [selectedDesignId, setSelectedDesignId] = useState('')
  const [designs, setDesigns] = useState([])
  const [savingDesign, setSavingDesign] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [notification, setNotification] = useState(null)
  const [layout, setLayout] = useState({
    pageSize: 'A4',
    labelWidth: 70,
    labelHeight: 35,
    margin: 5,
    gapX: 2,
    gapY: 2,
    showBorder: true
  })
  const [fields, setFields] = useState({
    logo: false,
    name: true,
    variant: true,
    price: true,
    sku: true,
    serial: false,
    barcode: true,
    qr: true
  })
  const mediaLibsRef = useRef(null)
  const barcodeCacheRef = useRef(new Map())
  const qrCacheRef = useRef(new Map())

  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products
    const query = searchQuery.toLowerCase()
    return (products || []).filter((product) => {
      return (
        (product.name || '').toLowerCase().includes(query) ||
        (product.sku || '').toLowerCase().includes(query)
      )
    })
  }, [products, searchQuery])

  const selectedProduct = useMemo(() => {
    if (!selectedProductId) return null
    return (products || []).find(p => p.id === selectedProductId) || null
  }, [products, selectedProductId])

  const variantOptions = useMemo(() => {
    if (!selectedProduct) return []
    const variants = Array.isArray(selectedProduct.variants) ? selectedProduct.variants : []
    if (variants.length === 0) {
      return [{
        key: 'base',
        label: 'Sin variantes',
        variant: null
      }]
    }
    return variants.map((variant, index) => ({
      key: `v-${index}`,
      label: buildVariantLabel(variant),
      variant
    }))
  }, [selectedProduct])

  useEffect(() => {
    if (!selectedProduct) {
      setSelectedVariantKeys([])
      setVariantQuantities({})
      return
    }
    const keys = variantOptions.map(option => option.key)
    const nextQuantities = {}
    keys.forEach(key => {
      nextQuantities[key] = 1
    })
    setSelectedVariantKeys(keys)
    setVariantQuantities(nextQuantities)
  }, [selectedProduct, variantOptions])

  useEffect(() => {
    let active = true
    const loadDesigns = async () => {
      try {
        const data = await getCollection('labelDesigns')
        if (active) setDesigns(data || [])
      } catch (err) {
        if (active) {
          setNotification({ type: 'error', message: err.message || 'Error cargando diseños' })
          setTimeout(() => setNotification(null), 4000)
        }
      }
    }
    loadDesigns()
    return () => {
      active = false
    }
  }, [])

  const getMediaLibs = async () => {
    if (mediaLibsRef.current) return mediaLibsRef.current
    const [barcodeMod, qrMod] = await Promise.all([import('jsbarcode'), import('qrcode')])
    const libs = {
      barcode: barcodeMod.default || barcodeMod,
      qr: qrMod.default || qrMod
    }
    mediaLibsRef.current = libs
    return libs
  }

  const getBarcodeDataUrl = async (value) => {
    if (!value) return null
    const cached = barcodeCacheRef.current.get(value)
    if (cached) return cached
    const { barcode } = await getMediaLibs()
    const canvas = document.createElement('canvas')
    barcode(canvas, value, {
      format: 'CODE128',
      displayValue: false,
      height: 50,
      margin: 0
    })
    const dataUrl = canvas.toDataURL('image/png')
    barcodeCacheRef.current.set(value, dataUrl)
    return dataUrl
  }

  const getQrDataUrl = async (value) => {
    if (!value) return null
    const cached = qrCacheRef.current.get(value)
    if (cached) return cached
    const { qr } = await getMediaLibs()
    const dataUrl = await qr.toDataURL(value, { margin: 1, width: 200 })
    qrCacheRef.current.set(value, dataUrl)
    return dataUrl
  }

  const toggleVariant = (key) => {
    setSelectedVariantKeys(prev => {
      if (prev.includes(key)) return prev.filter(v => v !== key)
      return [...prev, key]
    })
  }

  const handleQuantityChange = (key, value) => {
    const qty = Math.max(0, Number(value) || 0)
    setVariantQuantities(prev => ({ ...prev, [key]: qty }))
  }

  const handleSaveDesign = async () => {
    if (!designName.trim()) {
      setNotification({ type: 'error', message: 'Nombre de diseño requerido' })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    setSavingDesign(true)
    try {
      if (selectedDesignId) {
        await updateDocument('labelDesigns', selectedDesignId, {
          name: designName.trim(),
          layout,
          fields
        })
      } else {
        const id = await createDocument('labelDesigns', {
          name: designName.trim(),
          layout,
          fields
        })
        setSelectedDesignId(id)
      }
      const data = await getCollection('labelDesigns')
      setDesigns(data || [])
      setNotification({ type: 'success', message: 'Diseño guardado' })
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Error guardando diseño' })
      setTimeout(() => setNotification(null), 4000)
    } finally {
      setSavingDesign(false)
    }
  }

  const handleDeleteDesign = async (designId) => {
    setSavingDesign(true)
    try {
      await deleteDocument('labelDesigns', designId)
      const data = await getCollection('labelDesigns')
      setDesigns(data || [])
      if (selectedDesignId === designId) {
        setSelectedDesignId('')
        setDesignName('')
      }
      setNotification({ type: 'success', message: 'Diseño eliminado' })
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Error eliminando diseño' })
      setTimeout(() => setNotification(null), 4000)
    } finally {
      setSavingDesign(false)
    }
  }

  const handleApplyDesign = (design) => {
    if (!design) return
    setSelectedDesignId(design.id)
    setDesignName(design.name || '')
    setLayout(design.layout || layout)
    setFields(design.fields || fields)
  }

  const buildVariantPrice = (variant, basePrice) => {
    if (variant?.price != null) return Number(variant.price)
    if (variant?.priceDelta != null) return Number(basePrice || 0) + Number(variant.priceDelta || 0)
    return Number(basePrice || 0)
  }

  const buildQrValue = (product, variant) => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''
    const url = `${baseUrl}/producto/${product.id}`
    if (!variant?.selection) return url
    const params = new URLSearchParams()
    if (variant.selection.color) params.set('color', variant.selection.color)
    if (variant.selection.size) params.set('talle', variant.selection.size)
    const query = params.toString()
    return query ? `${url}?${query}` : url
  }

  const previewItem = useMemo(() => {
    if (!selectedProduct) return null
    const option = variantOptions.find(optionItem => selectedVariantKeys.includes(optionItem.key)) || variantOptions[0]
    const variant = option?.variant || null
    const priceValue = buildVariantPrice(variant, selectedProduct.price)
    return {
      name: selectedProduct.name || '',
      variantLabel: option?.label || '',
      sku: variant?.sku || selectedProduct.sku || '',
      serialNumber: variant?.serialNumber || '',
      priceLabel: formatCurrency(priceValue || 0)
    }
  }, [selectedProduct, selectedVariantKeys, variantOptions])

  const handleGenerate = async () => {
    if (!selectedProduct) {
      setNotification({ type: 'error', message: 'Seleccioná un producto' })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    const activeVariants = variantOptions.filter(option => selectedVariantKeys.includes(option.key))
    if (activeVariants.length === 0) {
      setNotification({ type: 'error', message: 'Seleccioná al menos una variante' })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    setGenerating(true)
    try {
      const items = []
      for (const option of activeVariants) {
        const qty = Math.max(0, Number(variantQuantities[option.key] || 0))
        if (qty === 0) continue
        const variant = option.variant
        const sku = variant?.sku || selectedProduct.sku || ''
        const serial = variant?.serialNumber || ''
        const priceValue = buildVariantPrice(variant, selectedProduct.price)
        const priceLabel = formatCurrency(priceValue || 0)
        const variantLabel = option.label
        const barcodeValue = sku || serial || selectedProduct.id
        const qrValue = buildQrValue(selectedProduct, variant)
        const barcodeDataUrl = fields.barcode ? await getBarcodeDataUrl(barcodeValue) : null
        const qrDataUrl = fields.qr ? await getQrDataUrl(qrValue) : null
        for (let i = 0; i < qty; i += 1) {
          items.push({
            name: selectedProduct.name || '',
            variantLabel,
            sku,
            serialNumber: serial,
            priceLabel,
            barcodeDataUrl,
            qrDataUrl
          })
        }
      }
      if (items.length === 0) {
        setNotification({ type: 'error', message: 'No hay etiquetas para generar' })
        setTimeout(() => setNotification(null), 3000)
        return
      }
      await generateLabelsPDF({
        items,
        layout,
        fields,
        fileName: `etiquetas-${selectedProduct.sku || selectedProduct.id}.pdf`
      })
      setNotification({ type: 'success', message: 'PDF generado correctamente' })
      setTimeout(() => setNotification(null), 3000)
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Error generando PDF' })
      setTimeout(() => setNotification(null), 4000)
    } finally {
      setGenerating(false)
    }
  }

  if (!can(MODULES.LABELS, ACTIONS.VIEW)) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Etiquetas</h3>
        <p className="text-slate-500 dark:text-slate-300 mt-2">No tenés permisos para acceder a este módulo.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">Etiquetas</h2>
          <p className="text-sm text-slate-500 dark:text-slate-300">Diseñá etiquetas y generá PDFs listos para impresión.</p>
        </div>
        <Button onClick={handleGenerate} disabled={generating || loading}>
          {generating ? 'Generando...' : 'Generar PDF'}
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="flex flex-col gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Producto</h3>
            <div className="flex flex-col gap-3">
              <input
                type="text"
                placeholder="Buscar por nombre o SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3 text-sm text-slate-700 dark:text-slate-200 outline-none"
              />
              <select
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3 text-sm text-slate-700 dark:text-slate-200 outline-none"
              >
                <option value="">Seleccionar producto</option>
                {filteredProducts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} {product.sku ? `(${product.sku})` : ''}
                  </option>
                ))}
              </select>
              {loading ? <p className="text-xs text-slate-400">Cargando productos...</p> : null}
              {error ? <p className="text-xs text-red-500">Error: {error}</p> : null}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Variantes</h3>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setSelectedVariantKeys(variantOptions.map(option => option.key))}
                >
                  Seleccionar todas
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setSelectedVariantKeys([])}
                >
                  Limpiar
                </Button>
              </div>
            </div>
            <div className="grid gap-3">
              {variantOptions.length === 0 ? (
                <p className="text-sm text-slate-400">Seleccioná un producto para ver variantes.</p>
              ) : (
                variantOptions.map(option => (
                  <div key={option.key} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
                    <label className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-200">
                      <input
                        type="checkbox"
                        checked={selectedVariantKeys.includes(option.key)}
                        onChange={() => toggleVariant(option.key)}
                      />
                      {option.label}
                    </label>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>Cantidad</span>
                      <input
                        type="number"
                        min="0"
                        value={variantQuantities[option.key] ?? 0}
                        onChange={(e) => handleQuantityChange(option.key, e.target.value)}
                        className="w-20 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-200"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Campos de etiqueta</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-slate-700 dark:text-slate-200">
              {FIELD_ORDER.filter(key => key in fields).map((key) => (
                <label key={key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={fields[key]}
                    onChange={(e) => setFields(prev => ({ ...prev, [key]: e.target.checked }))}
                  />
                  {FIELD_LABELS[key]}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Formato</h3>
            <div className="grid gap-3 text-sm">
              <label className="flex flex-col gap-1 text-slate-600 dark:text-slate-300">
                Tamaño de hoja
                <select
                  value={layout.pageSize}
                  onChange={(e) => setLayout(prev => ({ ...prev, pageSize: e.target.value }))}
                  className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                >
                  {Object.keys(PAGE_SIZES).map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-slate-600 dark:text-slate-300">
                  Ancho etiqueta (mm)
                  <input
                    type="number"
                    min="10"
                    value={layout.labelWidth}
                    onChange={(e) => setLayout(prev => ({ ...prev, labelWidth: Number(e.target.value) }))}
                    className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </label>
                <label className="flex flex-col gap-1 text-slate-600 dark:text-slate-300">
                  Alto etiqueta (mm)
                  <input
                    type="number"
                    min="10"
                    value={layout.labelHeight}
                    onChange={(e) => setLayout(prev => ({ ...prev, labelHeight: Number(e.target.value) }))}
                    className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </label>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <label className="flex flex-col gap-1 text-slate-600 dark:text-slate-300">
                  Margen (mm)
                  <input
                    type="number"
                    min="0"
                    value={layout.margin}
                    onChange={(e) => setLayout(prev => ({ ...prev, margin: Number(e.target.value) }))}
                    className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </label>
                <label className="flex flex-col gap-1 text-slate-600 dark:text-slate-300">
                  Separación X (mm)
                  <input
                    type="number"
                    min="0"
                    value={layout.gapX}
                    onChange={(e) => setLayout(prev => ({ ...prev, gapX: Number(e.target.value) }))}
                    className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </label>
                <label className="flex flex-col gap-1 text-slate-600 dark:text-slate-300">
                  Separación Y (mm)
                  <input
                    type="number"
                    min="0"
                    value={layout.gapY}
                    onChange={(e) => setLayout(prev => ({ ...prev, gapY: Number(e.target.value) }))}
                    className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={layout.showBorder}
                  onChange={(e) => setLayout(prev => ({ ...prev, showBorder: e.target.checked }))}
                />
                Mostrar borde de etiqueta
              </label>
              <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-700 p-4 text-xs text-slate-500">
                Hoja {layout.pageSize}: {PAGE_SIZES[layout.pageSize]?.width}×{PAGE_SIZES[layout.pageSize]?.height} mm
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Vista previa</h3>
            {previewItem ? (
              <div className="flex justify-center">
                <div
                  className="bg-white border border-dashed border-slate-300 rounded-lg p-3 text-[11px] text-slate-700"
                  style={{
                    width: 220,
                    height: Math.max(80, 220 * (Number(layout.labelHeight || 35) / Number(layout.labelWidth || 70)))
                  }}
                >
                  <div className="flex items-start gap-2">
                    {fields.logo ? <div className="w-10 h-4 bg-slate-200 rounded"></div> : null}
                    <div className="flex flex-col gap-1 flex-1">
                      {fields.name ? <div className="font-semibold">{previewItem.name || 'Producto'}</div> : null}
                      {fields.variant ? <div className="text-slate-500">{previewItem.variantLabel || 'Variante'}</div> : null}
                      {fields.price ? <div className="font-semibold">{previewItem.priceLabel}</div> : null}
                      {fields.sku ? <div className="text-slate-500">SKU: {previewItem.sku || '-'}</div> : null}
                      {fields.serial ? <div className="text-slate-500">Serie: {previewItem.serialNumber || '-'}</div> : null}
                    </div>
                  </div>
                  <div className="mt-2 flex items-end justify-between gap-2">
                    {fields.barcode ? <div className="h-6 flex-1 bg-slate-200 rounded"></div> : null}
                    {fields.qr ? <div className="w-8 h-8 bg-slate-200 rounded"></div> : null}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-400">Seleccioná un producto para ver la vista previa.</p>
            )}
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Diseños guardados</h3>
            <div className="flex flex-col gap-3">
              <input
                type="text"
                value={designName}
                onChange={(e) => setDesignName(e.target.value)}
                placeholder="Nombre del diseño"
                className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-700 dark:text-slate-200"
              />
              <div className="flex gap-2">
                <Button onClick={handleSaveDesign} disabled={savingDesign}>
                  {savingDesign ? 'Guardando...' : 'Guardar'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSelectedDesignId('')
                    setDesignName('')
                  }}
                  disabled={savingDesign}
                >
                  Nuevo
                </Button>
              </div>
              <div className="grid gap-2">
                {designs.length === 0 ? (
                  <p className="text-xs text-slate-400">No hay diseños guardados.</p>
                ) : (
                  designs.map(design => (
                    <div key={design.id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2">
                      <button
                        type="button"
                        className="text-left text-sm text-slate-700 dark:text-slate-200"
                        onClick={() => handleApplyDesign(design)}
                      >
                        {design.name || 'Sin nombre'}
                      </button>
                      <div className="flex gap-2">
                        <Button variant="secondary" size="small" onClick={() => handleApplyDesign(design)}>
                          Aplicar
                        </Button>
                        <Button variant="outline" size="small" onClick={() => handleDeleteDesign(design.id)} disabled={savingDesign}>
                          Borrar
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {notification ? (
        <Toast
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      ) : null}
    </div>
  )
}

export default AdminLabels
